import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const NewConflictCheck = () => {

    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState(null);

    const [formData, setFormData] = useState({
        prospective_client_name: '',
        prospective_client_type: 'INDIVIDUAL',
        prospective_matter_description: '',
        prospective_matter_type: '',
        search_terms: '',
        opposing_parties: '',
        related_parties: ''
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const token = localStorage.getItem('access_token');

            // Parse comma-separated values
            const payload = {
                prospective_client_name: formData.prospective_client_name,
                prospective_client_type: formData.prospective_client_type,
                prospective_matter_description: formData.prospective_matter_description,
                prospective_matter_type: formData.prospective_matter_type,
                search_terms: formData.search_terms
                    ? formData.search_terms.split(',').map(t => t.trim()).filter(t => t)
                    : [formData.prospective_client_name],
                opposing_parties: formData.opposing_parties
                    ? formData.opposing_parties.split(',').map(t => t.trim()).filter(t => t)
                    : [],
                related_parties: formData.related_parties
                    ? formData.related_parties.split(',').map(t => t.trim()).filter(t => t)
                    : []
            };

            const response = await fetch('/api/conflicts/checks/', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) throw new Error('Failed to run conflict check');

            const data = await response.json();
            setResults(data);
        } catch (err) {
            alert(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getConfidenceColor = (confidence) => {
        if (confidence >= 0.9) return 'text-red-600 bg-red-100';
        if (confidence >= 0.75) return 'text-orange-600 bg-orange-100';
        return 'text-yellow-600 bg-yellow-100';
    };

    const matterTypes = [
        'Civil Litigation',
        'Criminal Defense',
        'Corporate Law',
        'Family Law',
        'Property Law',
        'Employment Law',
        'Tax Law',
        'Estate Planning',
        'Personal Injury',
        'Contract Dispute',
        'Other'
    ];

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="mb-6">
                <nav className="text-sm text-gray-500 mb-2">
                    <Link to="/conflicts" className="hover:text-blue-600">Conflict Check</Link>
                    <span className="mx-2">›</span>
                    <span>New Check</span>
                </nav>
                <h1 className="text-2xl font-bold text-gray-900">New Conflict Check</h1>
                <p className="text-gray-600 mt-1">Before accepting new clients or matters</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Form */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Prospective Client */}
                        <div>
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Prospective Client</h3>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Client Name *
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.prospective_client_name}
                                        onChange={(e) => setFormData({ ...formData, prospective_client_name: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                        placeholder="Full name or company name"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Client Type
                                    </label>
                                    <select
                                        value={formData.prospective_client_type}
                                        onChange={(e) => setFormData({ ...formData, prospective_client_type: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                    >
                                        <option value="INDIVIDUAL">Individual</option>
                                        <option value="COMPANY">Company</option>
                                        <option value="GOVERNMENT">Government Entity</option>
                                        <option value="OTHER">Other</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Prospective Matter */}
                        <div>
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Prospective Matter</h3>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Matter Type
                                    </label>
                                    <select
                                        value={formData.prospective_matter_type}
                                        onChange={(e) => setFormData({ ...formData, prospective_matter_type: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                    >
                                        <option value="">Select Type</option>
                                        {matterTypes.map(type => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Matter Description
                                    </label>
                                    <textarea
                                        value={formData.prospective_matter_description}
                                        onChange={(e) => setFormData({ ...formData, prospective_matter_description: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                        rows="2"
                                        placeholder="Brief description of the matter"
                                    ></textarea>
                                </div>
                            </div>
                        </div>

                        {/* Search Terms */}
                        <div>
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Search Parameters</h3>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Additional Search Terms
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.search_terms}
                                        onChange={(e) => setFormData({ ...formData, search_terms: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                        placeholder="Aliases, former names (comma-separated)"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Client name is automatically included
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Opposing Parties
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.opposing_parties}
                                        onChange={(e) => setFormData({ ...formData, opposing_parties: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                        placeholder="Known adverse parties (comma-separated)"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Related Parties
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.related_parties}
                                        onChange={(e) => setFormData({ ...formData, related_parties: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                        placeholder="Witnesses, affiliates, etc. (comma-separated)"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="pt-4 border-t border-gray-200">
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
                            >
                                {loading ? (
                                    <span className="flex items-center justify-center">
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Running Check...
                                    </span>
                                ) : (
                                    'Run Conflict Check'
                                )}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Results */}
                <div>
                    {results ? (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                            {/* Status Header */}
                            <div className={`p-6 ${results.status === 'CLEARED'
                                ? 'bg-green-50 border-b border-green-100'
                                : 'bg-yellow-50 border-b border-yellow-100'
                                }`}>
                                <div className="flex items-center">
                                    {results.status === 'CLEARED' ? (
                                        <svg className="w-10 h-10 text-green-600 mr-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    ) : (
                                        <svg className="w-10 h-10 text-yellow-600 mr-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                    )}
                                    <div>
                                        <h3 className={`text-xl font-bold ${results.status === 'CLEARED' ? 'text-green-800' : 'text-yellow-800'
                                            }`}>
                                            {results.status === 'CLEARED' ? 'No Conflicts Found' : 'Potential Conflicts Detected'}
                                        </h3>
                                        <p className={results.status === 'CLEARED' ? 'text-green-600' : 'text-yellow-600'}>
                                            {results.potential_conflicts?.length || 0} potential match(es) found
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Conflicts List */}
                            {results.potential_conflicts?.length > 0 && (
                                <div className="p-6">
                                    <h4 className="font-medium text-gray-900 mb-4">Potential Matches</h4>
                                    <div className="space-y-3">
                                        {results.potential_conflicts.map((conflict, idx) => (
                                            <div key={idx} className="border border-gray-200 rounded-lg p-4">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <p className="font-medium text-gray-900">{conflict.matched_name}</p>
                                                        <p className="text-sm text-gray-500">
                                                            Searched: "{conflict.search_term}" • Type: {conflict.party_type}
                                                        </p>
                                                        <p className="text-sm text-gray-500">
                                                            Source: {conflict.source} • Match Type: {conflict.match_type}
                                                        </p>
                                                    </div>
                                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getConfidenceColor(conflict.confidence)}`}>
                                                        {Math.round(conflict.confidence * 100)}%
                                                    </span>
                                                </div>
                                                {conflict.details && (
                                                    <div className="mt-2 text-sm text-gray-600 bg-gray-50 rounded p-2">
                                                        {conflict.details.client_name && (
                                                            <p>Client: {conflict.details.client_name}</p>
                                                        )}
                                                        {conflict.details.case_title && (
                                                            <p>Case: {conflict.details.case_title}</p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="p-6 bg-gray-50 border-t border-gray-100">
                                <div className="flex gap-3">
                                    <Link
                                        to={`/conflicts/${results.id}`}
                                        className="flex-1 px-4 py-2 bg-blue-600 text-white text-center rounded-lg hover:bg-blue-700"
                                    >
                                        View Full Details
                                    </Link>
                                    <button
                                        onClick={() => {
                                            setResults(null);
                                            setFormData({
                                                prospective_client_name: '',
                                                prospective_client_type: 'INDIVIDUAL',
                                                prospective_matter_description: '',
                                                prospective_matter_type: '',
                                                search_terms: '',
                                                opposing_parties: '',
                                                related_parties: ''
                                            });
                                        }}
                                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                                    >
                                        New Check
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
                            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Run a Conflict Check</h3>
                            <p className="text-gray-500">
                                Fill in the form and click "Run Conflict Check" to search for potential conflicts of interest
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NewConflictCheck;
